import React, { useState } from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
export function FAQPage() {
  // State to track which FAQ items are expanded
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  // Toggle expanded state for an FAQ item
  const toggleItem = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-730">
        <div className="text-center mb-12" data-id="element-731">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-732">
            Frequently Asked Questions
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-733">
            Find answers to common questions about our work, donations, and
            impact.
          </p>
        </div>
      </Section>
      {/* Donations & Tax Section */}
      <Section background="white" data-id="element-734">
        <div className="max-w-4xl mx-auto" data-id="element-735">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-8" data-id="element-736">
            Donations & Tax
          </h2>
          <div className="space-y-4" data-id="element-737">
            {/* FAQ Item 1 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-738">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('tax-deductible')} data-id="element-739">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-740">
                  Are donations tax-deductible and will I receive a receipt?
                </h3>
                {expandedItems['tax-deductible'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-741" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-742" />}
              </button>
              {expandedItems['tax-deductible'] && <div className="px-6 pb-6" data-id="element-743">
                  <p className="font-inter text-neutral-charcoal" data-id="element-744">
                    Yes, Farasha Medical Aid is a registered 501(c)(3) nonprofit
                    organization, so all donations are tax-deductible to the
                    extent allowed by law. You will receive an automatic email
                    receipt for online donations that you can use for tax
                    purposes. For donations of physical items, we can provide a
                    donation acknowledgment letter upon request.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 2 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-745">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('monthly-gift')} data-id="element-746">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-747">
                  Can I make a monthly gift?
                </h3>
                {expandedItems['monthly-gift'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-748" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-749" />}
              </button>
              {expandedItems['monthly-gift'] && <div className="px-6 pb-6" data-id="element-750">
                  <p className="font-inter text-neutral-charcoal" data-id="element-751">
                    Absolutely! Monthly gifts provide sustainable support that
                    helps us plan our operations more effectively. When making a
                    donation online, simply select the "Monthly" option instead
                    of "One-time." You can adjust or cancel your monthly
                    donation at any time by contacting us at{' '}
                    <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline" data-id="element-752">
                      farashamedicalaid@gmail.com
                    </a>
                    .
                  </p>
                </div>}
            </div>
            {/* FAQ Item 3 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-753">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('employer-matching')} data-id="element-754">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-755">
                  Do you accept employer matching gifts?
                </h3>
                {expandedItems['employer-matching'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-756" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-757" />}
              </button>
              {expandedItems['employer-matching'] && <div className="px-6 pb-6" data-id="element-758">
                  <p className="font-inter text-neutral-charcoal" data-id="element-759">
                    Yes, we gladly accept employer matching gifts. Many
                    companies offer programs that match employee donations to
                    eligible nonprofits, potentially doubling or even tripling
                    your impact. Check with your HR department to see if your
                    employer offers a matching gift program, and let us know if
                    you need any documentation from our end to facilitate the
                    process.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 4 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-760">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('amazon-receipt')} data-id="element-761">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-762">
                  If I buy from the Amazon Wish List, can I get a receipt?
                </h3>
                {expandedItems['amazon-receipt'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-763" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-764" />}
              </button>
              {expandedItems['amazon-receipt'] && <div className="px-6 pb-6" data-id="element-765">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-766">
                    Yes. Add your full name and email in the Amazon "gift
                    message" when checking out. We'll email you an
                    acknowledgment that you can use for tax purposes. Donors are
                    responsible for assigning fair-market value if needed for
                    tax deduction purposes.
                  </p>
                  <div className="bg-neutral-offwhite p-4 rounded-lg" data-id="element-767">
                    <p className="font-inter text-neutral-charcoal font-medium" data-id="element-768">
                      Tip: When purchasing from our Wish List, look for the "Add
                      a gift receipt" option during checkout, and include your
                      email address in the gift message to ensure we can send
                      you an acknowledgment.
                    </p>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </Section>
      {/* Medical Supplies Section */}
      <Section background="sand" data-id="element-769">
        <div className="max-w-4xl mx-auto" data-id="element-770">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-8" data-id="element-771">
            Medical Supplies
          </h2>
          <div className="space-y-4" data-id="element-772">
            {/* FAQ Item 1 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden bg-white" data-id="element-773">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('accepted-items')} data-id="element-774">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-775">
                  What items do you accept?
                </h3>
                {expandedItems['accepted-items'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-776" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-777" />}
              </button>
              {expandedItems['accepted-items'] && <div className="px-6 pb-6" data-id="element-778">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-779">
                    We accept unopened, unexpired medical supplies that meet our
                    quality standards. This includes, but is not limited to:
                  </p>
                  <ul className="list-disc pl-5 mb-4 space-y-2 font-inter text-neutral-charcoal" data-id="element-780">
                    <li data-id="element-781">Bandages, gauze, and wound care supplies</li>
                    <li data-id="element-782">Personal protective equipment (masks, gloves, etc.)</li>
                    <li data-id="element-783">
                      Basic medications (over-the-counter and prescription)
                    </li>
                    <li data-id="element-784">
                      Diagnostic equipment (stethoscopes, blood pressure cuffs,
                      etc.)
                    </li>
                    <li data-id="element-785">Dental supplies and instruments</li>
                    <li data-id="element-786">Surgical instruments and supplies</li>
                    <li data-id="element-787">Medical textbooks (recent editions only)</li>
                  </ul>
                  <p className="font-inter text-neutral-charcoal" data-id="element-788">
                    For a complete list of accepted items, please visit our{' '}
                    <a href="/get-involved" className="text-primary-royal-blue hover:underline" data-id="element-789">
                      Get Involved
                    </a>{' '}
                    page or contact us directly.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 2 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden bg-white" data-id="element-790">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('open-boxes')} data-id="element-791">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-792">
                  Do you accept partially used or open boxes?
                </h3>
                {expandedItems['open-boxes'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-793" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-794" />}
              </button>
              {expandedItems['open-boxes'] && <div className="px-6 pb-6" data-id="element-795">
                  <p className="font-inter text-neutral-charcoal" data-id="element-796">
                    No, to protect patient safety, we can only accept unopened,
                    sealed medical supplies in their original packaging. This
                    ensures that all items meet quality and safety standards
                    when they reach the communities we serve. For medications,
                    we require that they are not expired or within 6 months of
                    their expiration date.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 3 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden bg-white" data-id="element-797">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('pickup-donations')} data-id="element-798">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-799">
                  Can you pick up donations?
                </h3>
                {expandedItems['pickup-donations'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-800" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-801" />}
              </button>
              {expandedItems['pickup-donations'] && <div className="px-6 pb-6" data-id="element-802">
                  <p className="font-inter text-neutral-charcoal" data-id="element-803">
                    We consider pickups on a case-by-case basis, depending on
                    the volume of donations and our volunteer availability. For
                    large donations from healthcare facilities or organizations,
                    we may be able to arrange a pickup. Please contact us at{' '}
                    <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline" data-id="element-804">
                      farashamedicalaid@gmail.com
                    </a>{' '}
                    with details about your donation, and we'll work with you to
                    find the best solution.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 4 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden bg-white" data-id="element-805">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('amazon-wishlist')} data-id="element-806">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-807">
                  Do you have an Amazon Wish List of most-needed items?
                </h3>
                {expandedItems['amazon-wishlist'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-808" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-809" />}
              </button>
              {expandedItems['amazon-wishlist'] && <div className="px-6 pb-6" data-id="element-810">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-811">
                    Yes, we maintain an Amazon Wish List that is regularly
                    updated based on the current needs of the clinics we serve.
                    This is one of the easiest ways to contribute, as items are
                    shipped directly to us.
                  </p>
                  <div className="flex justify-center mb-4" data-id="element-812">
                    <Button className="flex items-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-813">
                      View Amazon Wish List <ExternalLink size={16} data-id="element-814" />
                    </Button>
                  </div>
                  <p className="font-inter text-neutral-charcoal text-sm" data-id="element-815">
                    The list is updated as clinic needs change. Please include
                    your email in the gift message if you'd like to receive an
                    acknowledgment for your donation.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </Section>
      {/* Shipping & Impact Section */}
      <Section background="white" data-id="element-816">
        <div className="max-w-4xl mx-auto" data-id="element-817">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-8" data-id="element-818">
            Shipping & Impact
          </h2>
          <div className="space-y-4" data-id="element-819">
            {/* FAQ Item 1 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-820">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('where-supplies-go')} data-id="element-821">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-822">
                  Where do supplies go and how quickly do they arrive?
                </h3>
                {expandedItems['where-supplies-go'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-823" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-824" />}
              </button>
              {expandedItems['where-supplies-go'] && <div className="px-6 pb-6" data-id="element-825">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-826">
                    Medical supplies are shipped to Coptic Orthodox monastery
                    clinics and community healthcare centers across Egypt. These
                    clinics serve underserved communities where healthcare
                    resources are limited.
                  </p>
                  <p className="font-inter text-neutral-charcoal" data-id="element-827">
                    The shipping process typically takes 4-6 weeks, including
                    collection, sorting, packing, customs clearance, and final
                    delivery. We coordinate with trusted partners on the ground
                    to ensure supplies reach their intended destinations and are
                    put to immediate use.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 2 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-828">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('quality-control')} data-id="element-829">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-830">
                  How do you ensure quality and inventory control?
                </h3>
                {expandedItems['quality-control'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-831" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-832" />}
              </button>
              {expandedItems['quality-control'] && <div className="px-6 pb-6" data-id="element-833">
                  <p className="font-inter text-neutral-charcoal" data-id="element-834">
                    We have a rigorous quality control process. All donated
                    supplies are inspected by trained volunteers to ensure they
                    meet our standards: unopened, unexpired, and appropriate for
                    use. We maintain detailed inventory records of all items
                    received and shipped, allowing us to track our impact and
                    ensure accountability. Additionally, we work closely with
                    recipient clinics to confirm receipt and proper use of
                    supplies.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 3 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-835">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('donation-impact')} data-id="element-836">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-837">
                  What does $25/$100/$500 accomplish?
                </h3>
                {expandedItems['donation-impact'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-838" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-839" />}
              </button>
              {expandedItems['donation-impact'] && <div className="px-6 pb-6" data-id="element-840">
                  <div className="grid md:grid-cols-3 gap-4 mb-4" data-id="element-841">
                    <div className="bg-neutral-offwhite p-4 rounded-lg" data-id="element-842">
                      <h4 className="font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-843">
                        $25 can:
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 font-inter text-neutral-charcoal" data-id="element-844">
                        <li data-id="element-845">Ship a box of basic medical supplies</li>
                        <li data-id="element-846">
                          Provide bandages and wound care for approximately 10
                          patients
                        </li>
                        <li data-id="element-847">
                          Cover customs fees for a small shipment of critical
                          items
                        </li>
                      </ul>
                    </div>
                    <div className="bg-neutral-offwhite p-4 rounded-lg" data-id="element-848">
                      <h4 className="font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-849">
                        $100 can:
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 font-inter text-neutral-charcoal" data-id="element-850">
                        <li data-id="element-851">
                          Supply essential medications for a week at a small
                          clinic
                        </li>
                        <li data-id="element-852">
                          Provide personal protective equipment for healthcare
                          workers
                        </li>
                        <li data-id="element-853">
                          Fund the sorting and packing of supplies for a medium
                          shipment
                        </li>
                      </ul>
                    </div>
                    <div className="bg-neutral-offwhite p-4 rounded-lg" data-id="element-854">
                      <h4 className="font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-855">
                        $500 can:
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 font-inter text-neutral-charcoal" data-id="element-856">
                        <li data-id="element-857">Ship a large pallet of diverse medical supplies</li>
                        <li data-id="element-858">
                          Provide specialized equipment for diagnostic services
                        </li>
                        <li data-id="element-859">
                          Support a month of operations at a small monastery
                          clinic
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p className="font-inter text-neutral-charcoal" data-id="element-860">
                    Every donation, regardless of size, contributes to our
                    mission of bridging healthcare gaps and providing quality
                    care to underserved communities.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 4 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-861">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('amazon-delivery')} data-id="element-862">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-863">
                  How do Amazon Wish List purchases get delivered if you don't
                  list an address?
                </h3>
                {expandedItems['amazon-delivery'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-864" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-865" />}
              </button>
              {expandedItems['amazon-delivery'] && <div className="px-6 pb-6" data-id="element-866">
                  <p className="font-inter text-neutral-charcoal" data-id="element-867">
                    Amazon masks the receiving address for privacy and security
                    purposes. When you purchase items from our Wish List, they
                    are automatically shipped to our designated receiving point
                    without revealing the physical address to donors. This
                    system allows us to maintain security while still receiving
                    your generous contributions. Rest assured that all items
                    purchased from our Wish List reach us directly.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </Section>
      {/* Volunteering Section */}
      <Section background="sand" data-id="element-868">
        <div className="max-w-4xl mx-auto" data-id="element-869">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-8" data-id="element-870">
            Volunteering
          </h2>
          <div className="space-y-4" data-id="element-871">
            {/* FAQ Item 1 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden bg-white" data-id="element-872">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('volunteer-opportunities')} data-id="element-873">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-874">
                  What can volunteers do? Are there age or time requirements?
                </h3>
                {expandedItems['volunteer-opportunities'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-875" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-876" />}
              </button>
              {expandedItems['volunteer-opportunities'] && <div className="px-6 pb-6" data-id="element-877">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-878">
                    Volunteers are essential to our operations! There are many
                    ways to get involved:
                  </p>
                  <ul className="list-disc pl-5 mb-4 space-y-2 font-inter text-neutral-charcoal" data-id="element-879">
                    <li data-id="element-880">Sorting and inventorying donated supplies</li>
                    <li data-id="element-881">Packing and preparing shipments</li>
                    <li data-id="element-882">Organizing and hosting supply drives</li>
                    <li data-id="element-883">Community outreach and education</li>
                    <li data-id="element-884">Administrative and technical support</li>
                    <li data-id="element-885">Translation services (Arabic/English)</li>
                  </ul>
                  <p className="font-inter text-neutral-charcoal" data-id="element-886">
                    We welcome volunteers ages 16 and up, with parental
                    supervision required for those under 18. Time commitments
                    are flexible—you can volunteer for one-time events or on a
                    regular schedule. We work with your availability to find the
                    best fit for your skills and interests.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 2 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden bg-white" data-id="element-887">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('host-drive')} data-id="element-888">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-889">
                  Can I host a supply drive at work/school/church?
                </h3>
                {expandedItems['host-drive'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-890" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-891" />}
              </button>
              {expandedItems['host-drive'] && <div className="px-6 pb-6" data-id="element-892">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-893">
                    Absolutely! Hosting a supply drive is a wonderful way to
                    engage your community in our mission. We provide a
                    comprehensive Host-a-Drive Toolkit with everything you need
                    to organize a successful drive, including:
                  </p>
                  <ul className="list-disc pl-5 mb-4 space-y-1 font-inter text-neutral-charcoal" data-id="element-894">
                    <li data-id="element-895">Planning timeline and checklist</li>
                    <li data-id="element-896">Printable flyers and collection box labels</li>
                    <li data-id="element-897">List of most-needed items</li>
                    <li data-id="element-898">Email templates and social media content</li>
                    <li data-id="element-899">Guidelines for collecting and delivering donations</li>
                  </ul>
                  <div className="flex justify-center mb-4" data-id="element-900">
                    <Button href="/downloads/host-a-drive-toolkit.pdf" data-id="element-901">
                      Download Drive Toolkit (PDF)
                    </Button>
                  </div>
                  <p className="font-inter text-neutral-charcoal text-sm" data-id="element-902">
                    Please contact us at{' '}
                    <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline" data-id="element-903">
                      farashamedicalaid@gmail.com
                    </a>{' '}
                    to let us know about your drive so we can provide any
                    additional support you might need.
                  </p>
                </div>}
            </div>
            {/* FAQ Item 3 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden bg-white" data-id="element-904">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('share-wishlist')} data-id="element-905">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-906">
                  Can I help by sharing the Wish List?
                </h3>
                {expandedItems['share-wishlist'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-907" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-908" />}
              </button>
              {expandedItems['share-wishlist'] && <div className="px-6 pb-6" data-id="element-909">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-910">
                    Yes—sharing the link and organizing team drives around the
                    Wish List has a big impact! Sharing our Amazon Wish List is
                    one of the simplest yet most effective ways to support our
                    mission.
                  </p>
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-911">
                    Consider sharing the list with your social networks,
                    workplace, faith community, or family. Some supporters even
                    organize "virtual drives" where a group collectively
                    purchases items from the list.
                  </p>
                  <div className="flex justify-center mb-4" data-id="element-912">
                    <Button className="flex items-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-913">
                      View Amazon Wish List <ExternalLink size={16} data-id="element-914" />
                    </Button>
                  </div>
                  <p className="font-inter text-neutral-charcoal text-sm" data-id="element-915">
                    Remember to remind purchasers to include their email in the
                    gift message if they'd like to receive an acknowledgment for
                    their donation.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </Section>
      {/* Partnerships Section */}
      <Section background="white" data-id="element-916">
        <div className="max-w-4xl mx-auto" data-id="element-917">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-8" data-id="element-918">
            Partnerships
          </h2>
          <div className="space-y-4" data-id="element-919">
            {/* FAQ Item 1 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-920">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('partner-opportunities')} data-id="element-921">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-922">
                  How can hospitals/companies/clinics partner with you?
                </h3>
                {expandedItems['partner-opportunities'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-923" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-924" />}
              </button>
              {expandedItems['partner-opportunities'] && <div className="px-6 pb-6" data-id="element-925">
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-926">
                    We offer several partnership opportunities:
                  </p>
                  <ul className="list-disc pl-5 mb-4 space-y-2 font-inter text-neutral-charcoal" data-id="element-927">
                    <li data-id="element-928">
                      <strong data-id="element-929">Healthcare Providers:</strong> Hospitals and
                      clinics can establish regular donation programs for unused
                      supplies or provide expertise and guidance.
                    </li>
                    <li data-id="element-930">
                      <strong data-id="element-931">Corporations:</strong> Businesses can support us
                      through financial sponsorships, employee volunteer
                      programs, matching gift initiatives, or in-kind donations.
                    </li>
                    <li data-id="element-932">
                      <strong data-id="element-933">Medical Manufacturers:</strong> Companies can
                      donate overstock, discontinued items, or slightly
                      imperfect products that meet quality standards.
                    </li>
                    <li data-id="element-934">
                      <strong data-id="element-935">Community Organizations:</strong> Faith
                      communities, service clubs, and community groups can
                      organize drives or provide volunteer support.
                    </li>
                  </ul>
                  <p className="font-inter text-neutral-charcoal mb-4" data-id="element-936">
                    We tailor each partnership to align with your organization's
                    goals and capabilities. To explore partnership
                    opportunities, please visit our{' '}
                    <a href="/partners" className="text-primary-royal-blue hover:underline" data-id="element-937">
                      Partners
                    </a>{' '}
                    page or contact us directly at{' '}
                    <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline" data-id="element-938">
                      farashamedicalaid@gmail.com
                    </a>
                    .
                  </p>
                </div>}
            </div>
            {/* FAQ Item 2 */}
            <div className="border border-neutral-charcoal/10 rounded-xl overflow-hidden" data-id="element-939">
              <button className="w-full flex justify-between items-center p-6 text-left focus:outline-none" onClick={() => toggleItem('impact-reports')} data-id="element-940">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-941">
                  Do you provide impact reports for partners?
                </h3>
                {expandedItems['impact-reports'] ? <ChevronUp className="text-primary-royal-blue flex-shrink-0" data-id="element-942" /> : <ChevronDown className="text-primary-royal-blue flex-shrink-0" data-id="element-943" />}
              </button>
              {expandedItems['impact-reports'] && <div className="px-6 pb-6" data-id="element-944">
                  <p className="font-inter text-neutral-charcoal" data-id="element-945">
                    Yes, we provide detailed impact reports for our partners.
                    These reports include information on how your contributions
                    have been used, the communities they've reached, and the
                    difference they've made. We believe in transparency and
                    accountability, and we want our partners to see the tangible
                    results of their support. Reports can be customized based on
                    your organization's specific interests and reporting needs.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </Section>
      {/* Important Note */}
      <Section background="sand" data-id="element-946">
        <div className="max-w-4xl mx-auto" data-id="element-947">
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-948">
            <h2 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-949">
              Important Note About Donations
            </h2>
            <p className="font-inter text-neutral-charcoal text-lg font-medium" data-id="element-950">
              We do not publish a drop-off address. All drop-offs are scheduled
              by email at{' '}
              <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline" data-id="element-951">
                farashamedicalaid@gmail.com
              </a>
              .
            </p>
          </div>
        </div>
      </Section>
      {/* CTA Section */}
      <Section background="deep-blue" data-id="element-952">
        <div className="text-center" data-id="element-953">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-954">
            Still have questions?
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-955">
            We're here to help! Contact us for more information about our work
            or how you can get involved.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-956">
            <Button href="/contact" size="lg" data-id="element-957">
              Contact Us
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-958">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}